<script setup>
/* global axios */
// Lista de Flows (chatbot / sequência) — porta fiel da ChatbotFlows do original:
// header com ícone+subtítulo, tabela (flow/tipo/gatilho/status/execuções/ações), modal de criação
// com cards de tipo + multiseleção de caixas, modais de editar caixa e duplicar.
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import KanbanSelect from './KanbanSelect.vue';
import KanbanMultiSelect from './KanbanMultiSelect.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const flows = ref([]);
const inboxes = ref([]);
const loading = ref(true);
const error = ref('');

// busca / filtro / paginação
const search = ref('');
const typeFilter = ref('all'); // all | chatbot | sequence
const statusFilter = ref('all'); // all | active | inactive
const currentPage = ref(1);
const PAGE_SIZE = 20;

const typeOptions = computed(() => [
  { value: 'all', label: t('CHATBOT.toolbar.allTypes') },
  { value: 'chatbot', label: t('CHATBOT.flowType.chatbot') },
  { value: 'sequence', label: t('CHATBOT.flowType.sequence') },
]);
const statusOptions = computed(() => [
  { value: 'all', label: t('CHATBOT.toolbar.allStatus') },
  { value: 'active', label: t('CHATBOT.toolbar.active') },
  { value: 'inactive', label: t('CHATBOT.toolbar.inactive') },
]);
// opções de caixa para o multi-select (dropdown + busca) na criação/edição de flow
const inboxOptions = computed(() =>
  inboxes.value.map(i => ({ value: String(i.id), label: i.name }))
);

const filteredFlows = computed(() => {
  const q = search.value.trim().toLowerCase();
  return flows.value.filter(f => {
    if (q) {
      const hay = `${f.name || ''} ${f.description || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (typeFilter.value !== 'all') {
      const ft = f.flow_type === 'sequence' ? 'sequence' : 'chatbot';
      if (ft !== typeFilter.value) return false;
    }
    if (statusFilter.value === 'active' && !f.is_active) return false;
    if (statusFilter.value === 'inactive' && f.is_active) return false;
    return true;
  });
});
const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredFlows.value.length / PAGE_SIZE))
);
const pagedFlows = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return filteredFlows.value.slice(start, start + PAGE_SIZE);
});
const pageFrom = computed(() =>
  filteredFlows.value.length ? (currentPage.value - 1) * PAGE_SIZE + 1 : 0
);
const pageTo = computed(() =>
  Math.min(currentPage.value * PAGE_SIZE, filteredFlows.value.length)
);
// reseta a página ao mudar a busca/filtros; mantém a página válida ao deletar
watch([search, typeFilter, statusFilter], () => {
  currentPage.value = 1;
});
watch(totalPages, tp => {
  if (currentPage.value > tp) currentPage.value = tp;
});

// criação
const showCreate = ref(false);
const creating = ref(false);
const formError = ref('');
const form = ref({ name: '', description: '', type: 'chatbot', inboxIds: [] });

// rename / descrição inline
const renamingId = ref(null);
const renameValue = ref('');
const editingDescId = ref(null);
const descValue = ref('');

// modais editar caixa / duplicar / credenciais
const editInboxFlow = ref(null);
const editInboxIds = ref([]);
const duplicateFlow = ref(null);
const duplicateName = ref('');
const showAICreds = ref(false);
// credenciais de IA (CRUD real) — abas Texto / Áudio / Imagem
const AI_TABS = [
  {
    id: 'text',
    providers: [
      { id: 'openai', label: 'OpenAI' },
      { id: 'groq', label: 'Groq' },
      { id: 'openrouter', label: 'OpenRouter' },
      { id: 'google', label: 'Google Gemini' },
    ],
  },
  {
    id: 'audio',
    providers: [
      { id: 'openai', label: 'OpenAI Whisper' },
      { id: 'groq', label: 'Groq Whisper' },
    ],
  },
  {
    id: 'image',
    providers: [
      { id: 'openai', label: 'OpenAI DALL·E' },
      { id: 'stability', label: 'Stability AI' },
    ],
  },
];
const activeAiTab = ref('text');
const currentProviders = computed(
  () => AI_TABS.find(x => x.id === activeAiTab.value)?.providers || []
);
const aiCreds = ref([]);
const aiKeys = ref({});
const aiStatus = ref({});
const ck = (p, type) => `${type}:${p}`; // chave composta tipo:provider
const aiConfigured = (p, type = 'text') =>
  aiCreds.value.some(
    c => c.provider === p && c.credential_type === type && c.configured
  );
const loadAiCreds = async () => {
  try {
    aiCreds.value = (await axios.get(`${base}/ai_credentials`)).data.data || [];
  } catch (e) {
    aiCreds.value = [];
  }
};
const openAICreds = () => {
  showAICreds.value = true;
  activeAiTab.value = 'text';
  aiKeys.value = {};
  aiStatus.value = {};
  oauthStep.value = 'idle';
  oauthError.value = '';
  loadAiCreds();
  loadOauthStatus();
};
const saveAiCred = async (p, type = 'text') => {
  const k = ck(p, type);
  const key = (aiKeys.value[k] || '').trim();
  if (!key) return;
  aiStatus.value[k] = { kind: 'saving' };
  try {
    await axios.post(`${base}/ai_credentials`, {
      provider: p,
      credential_type: type,
      api_key: key,
    });
    aiKeys.value[k] = '';
    aiStatus.value[k] = { kind: 'saved' };
    await loadAiCreds();
  } catch (e) {
    aiStatus.value[k] = {
      kind: 'error',
      msg: t('CHATBOT.aiCredentialsModal.saveError'),
    };
  }
};
const testAiCred = async (p, type = 'text') => {
  const k = ck(p, type);
  aiStatus.value[k] = { kind: 'testing' };
  try {
    const r = (
      await axios.post(`${base}/ai_credentials/test`, {
        provider: p,
        credential_type: type,
      })
    ).data;
    aiStatus.value[k] = r.ok ? { kind: 'ok' } : { kind: 'error', msg: r.error };
  } catch (e) {
    aiStatus.value[k] = {
      kind: 'error',
      msg: t('CHATBOT.aiCredentialsModal.testError'),
    };
  }
};
const removeAiCred = async (p, type = 'text') => {
  const c = aiCreds.value.find(
    x => x.provider === p && x.credential_type === type
  );
  if (!c) return;
  await axios.delete(`${base}/ai_credentials/${c.id}`);
  aiStatus.value[ck(p, type)] = {};
  await loadAiCreds();
};

// conexão OAuth da conta ChatGPT (provider openai_oauth)
const oauthStatus = ref({ connected: false });
const oauthStep = ref('idle'); // 'idle' | 'waiting_paste'
const oauthCallbackUrl = ref('');
const oauthBusy = ref(false);
const oauthError = ref('');
const loadOauthStatus = async () => {
  try {
    oauthStatus.value =
      (await axios.get(`${base}/ai_oauth`)).data.data || { connected: false };
  } catch (e) {
    oauthStatus.value = { connected: false };
  }
};
const connectOauth = async () => {
  oauthError.value = '';
  oauthBusy.value = true;
  try {
    const { authUrl } = (await axios.get(`${base}/ai_oauth/auth_url`)).data.data;
    window.open(authUrl, 'chatgpt_auth', 'width=620,height=760,left=200,top=80');
    oauthStep.value = 'waiting_paste';
    oauthCallbackUrl.value = '';
  } catch (e) {
    oauthError.value = t('CHATBOT.aiCredentialsModal.oauth.connectError');
  } finally {
    oauthBusy.value = false;
  }
};
const exchangeOauth = async () => {
  const url = oauthCallbackUrl.value.trim();
  if (!url) return;
  oauthError.value = '';
  oauthBusy.value = true;
  try {
    await axios.post(`${base}/ai_oauth/exchange_code`, { callbackUrl: url });
    oauthStep.value = 'idle';
    oauthCallbackUrl.value = '';
    await loadOauthStatus();
  } catch (e) {
    oauthError.value =
      e.response?.data?.error || t('CHATBOT.aiCredentialsModal.oauth.exchangeError');
  } finally {
    oauthBusy.value = false;
  }
};
const cancelOauth = () => {
  oauthStep.value = 'idle';
  oauthCallbackUrl.value = '';
  oauthError.value = '';
};
const disconnectOauth = async () => {
  await axios.delete(`${base}/ai_oauth`);
  await loadOauthStatus();
};

// variáveis de flow (CRUD account-global)
const showVars = ref(false);
const variables = ref([]);
const newVar = ref({ name: '', default_value: '', description: '' });
const loadVariables = async () => {
  try {
    variables.value =
      (await axios.get(`${base}/flow_variables`)).data.data || [];
  } catch (e) {
    variables.value = [];
  }
};
const openVars = () => {
  showVars.value = true;
  newVar.value = { name: '', default_value: '', description: '' };
  loadVariables();
};
const addVariable = async () => {
  const name = newVar.value.name.trim().replace(/[^a-zA-Z0-9_]/g, '');
  if (!name) return;
  await axios.post(`${base}/flow_variables`, { ...newVar.value, name });
  newVar.value = { name: '', default_value: '', description: '' };
  await loadVariables();
};
const removeVariable = async v => {
  if (!window.confirm(t('CHATBOT.variablesModal.confirmDelete'))) return;
  await axios.delete(`${base}/flow_variables/${v.id}`);
  await loadVariables();
};
const braced = name => `{{${name}}}`;

const load = async () => {
  loading.value = true;
  try {
    flows.value = (await axios.get(`${base}/flows`)).data.data || [];
  } catch (e) {
    error.value = t('CHATBOT.errors.loadFlows');
  } finally {
    loading.value = false;
  }
};
const loadInboxes = async () => {
  try {
    inboxes.value =
      (await axios.get(`/api/v1/accounts/${accountId}/inboxes`)).data.payload ||
      [];
  } catch (e) {
    inboxes.value = [];
  }
};

const typeLabel = ft =>
  ft === 'sequence'
    ? t('CHATBOT.flowType.sequence')
    : t('CHATBOT.flowType.chatbot');
const triggerLabel = f => {
  const tr = f.trigger || {};
  if (tr.type === 'keyword' && tr.value)
    return t('CHATBOT.trigger.keyword', { value: tr.value });
  if (tr.type === 'inbox') {
    let val = [];
    if (Array.isArray(tr.value)) val = tr.value;
    else if (tr.value) val = [tr.value];
    if (!val.length) return `📱 ${t('CHATBOT.trigger.allInboxes')}`;
    if (val.length === 1) {
      const i = inboxes.value.find(x => String(x.id) === String(val[0]));
      return `📱 ${i ? i.name : t('CHATBOT.trigger.inboxId', { id: val[0] })}`;
    }
    return `📱 ${t('CHATBOT.inboxSelect.selectedCount', { count: val.length })}`;
  }
  return t('CHATBOT.trigger.firstMessage');
};

const openBuilder = f =>
  router.push(`/app/accounts/${accountId}/flows/${f.id}`);
const openHistory = f =>
  router.push(`/app/accounts/${accountId}/flows/${f.id}/history`);

// criar
const openCreate = () => {
  form.value = { name: '', description: '', type: 'chatbot', inboxIds: [] };
  formError.value = '';
  showCreate.value = true;
};
const createFlow = async () => {
  if (!form.value.name.trim()) {
    formError.value = t('CHATBOT.createModal.nameRequired');
    return;
  }
  creating.value = true;
  try {
    const { data } = await axios.post(`${base}/flows`, {
      name: form.value.name.trim(),
      description: form.value.description,
      flow_type: form.value.type,
      trigger: { type: 'inbox', value: form.value.inboxIds },
    });
    showCreate.value = false;
    openBuilder(data.data);
  } catch (e) {
    formError.value = t('CHATBOT.errors.createFlow', {
      message: e?.message || '',
    });
  } finally {
    creating.value = false;
  }
};

// toggle ativo
const toggleActive = async f => {
  await axios.patch(`${base}/flows/${f.id}`, { is_active: !f.is_active });
  f.is_active = !f.is_active;
};

// rename / descrição
const startRename = f => {
  renamingId.value = f.id;
  renameValue.value = f.name;
};
const commitRename = async f => {
  const v = renameValue.value.trim();
  renamingId.value = null;
  if (v && v !== f.name) {
    await axios.patch(`${base}/flows/${f.id}`, { name: v });
    f.name = v;
  }
};
const startDesc = f => {
  editingDescId.value = f.id;
  descValue.value = f.description || '';
};
const commitDesc = async f => {
  const v = descValue.value.trim();
  editingDescId.value = null;
  if (v !== (f.description || '')) {
    await axios.patch(`${base}/flows/${f.id}`, { description: v });
    f.description = v;
  }
};

// editar caixa
const openEditInbox = f => {
  editInboxFlow.value = f;
  const v = f.trigger?.value;
  let ids = [];
  if (Array.isArray(v)) ids = v.map(String);
  else if (v) ids = [String(v)];
  editInboxIds.value = ids;
};
const saveEditInbox = async () => {
  await axios.patch(`${base}/flows/${editInboxFlow.value.id}`, {
    trigger: { type: 'inbox', value: editInboxIds.value },
  });
  editInboxFlow.value.trigger = { type: 'inbox', value: editInboxIds.value };
  editInboxFlow.value = null;
};

// duplicar
const openDuplicate = f => {
  duplicateFlow.value = f;
  duplicateName.value = t('CHATBOT.duplicateModal.defaultName', {
    name: f.name,
  });
};
const saveDuplicate = async () => {
  await axios.post(`${base}/flows/${duplicateFlow.value.id}/duplicate`, {
    name: duplicateName.value.trim(),
  });
  duplicateFlow.value = null;
  await load();
};

const remove = async f => {
  if (!window.confirm(t('CHATBOT.confirmDelete', { name: f.name }))) return;
  await axios.delete(`${base}/flows/${f.id}`);
  await load();
};

// matricular contatos por telefone (só p/ flows do tipo sequência) — portado da
// antiga tela Sequências, que foi removida (o builder/edição já vive aqui).
const enrollFor = ref(null);
const enrollInbox = ref(null);
const enrollPhones = ref('');
const enrolling = ref(false);
const openEnroll = f => {
  enrollFor.value = f;
  enrollInbox.value = inboxes.value[0]?.id || null;
  enrollPhones.value = '';
};
const submitEnroll = async () => {
  const phones = enrollPhones.value
    .split(/[\n,;]+/)
    .map(p => p.trim())
    .filter(Boolean);
  if (!enrollInbox.value || !phones.length) return;
  enrolling.value = true;
  try {
    const { data } = await axios.post(
      `${base}/sequences/${enrollFor.value.id}/enroll`,
      { inbox_id: enrollInbox.value, phones }
    );
    useAlert(
      t('SEQUENCES.enroll.done', { enrolled: data.enrolled, total: data.total })
    );
    enrollFor.value = null;
    await load();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('SEQUENCES.enroll.error'));
  } finally {
    enrolling.value = false;
  }
};

onMounted(() => {
  load();
  loadInboxes();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- header -->
    <header class="bg-n-background border-b shrink-0 border-n-weak">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <svg
              class="w-8 h-8 text-n-blue-11 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="8" height="8" x="3" y="3" rx="2" />
              <path d="M7 11v4a2 2 0 0 0 2 2h4" />
              <rect width="8" height="8" x="13" y="13" rx="2" />
            </svg>
            <div>
              <h1 class="text-heading-1 text-n-slate-12">
                {{ t('CHATBOT.title') }}
              </h1>
              <p class="text-sm text-n-slate-11">{{ t('CHATBOT.subtitle') }}</p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg text-n-slate-11 bg-n-alpha-1 hover:bg-n-alpha-2"
              @click="openVars"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              {{ t('CHATBOT.variablesModal.button') }}
            </button>
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg text-n-iris-11 bg-n-alpha-1 hover:bg-n-alpha-2"
              @click="openAICreds"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              {{ t('CHATBOT.aiCredentials') }}
            </button>
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
              @click="openCreate"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {{ t('CHATBOT.newFlow') }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 p-6 overflow-auto">
      <div
        v-if="error"
        class="p-4 mb-6 border rounded-lg bg-red-50 border-red-200 text-sm text-red-800 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800"
      >
        {{ error }}
      </div>

      <div v-if="loading" class="flex justify-center py-12 text-n-slate-11">
        {{ t('CHATBOT.inboxSelect.loading') }}
      </div>

      <!-- empty state -->
      <div
        v-else-if="!flows.length"
        class="p-12 text-center bg-n-background rounded-lg shadow-sm"
      >
        <svg
          class="w-16 h-16 mx-auto mb-4 text-n-slate-10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect width="8" height="8" x="3" y="3" rx="2" />
          <path d="M7 11v4a2 2 0 0 0 2 2h4" />
          <rect width="8" height="8" x="13" y="13" rx="2" />
        </svg>
        <h3 class="text-heading-3 mb-2 text-n-slate-12">
          {{ t('CHATBOT.emptyState.title') }}
        </h3>
        <p class="mb-6 text-n-slate-11">
          {{ t('CHATBOT.emptyState.subtitle') }}
        </p>
        <button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 mx-auto"
          @click="openCreate"
        >
          {{ t('CHATBOT.emptyState.createButton') }}
        </button>
      </div>

      <!-- tabela -->
      <div
        v-else
        class="overflow-hidden bg-n-background border rounded-lg shadow-sm border-n-weak"
      >
        <!-- toolbar: busca + filtros -->
        <div
          class="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-n-weak"
        >
          <input
            v-model="search"
            type="text"
            :placeholder="t('CHATBOT.toolbar.search')"
            class="reset-base flex-1 min-w-[12rem] px-3 py-2 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12"
          />
          <div class="w-44 shrink-0">
            <KanbanSelect v-model="typeFilter" :options="typeOptions" />
          </div>
          <div class="w-44 shrink-0">
            <KanbanSelect v-model="statusFilter" :options="statusOptions" />
          </div>
        </div>
        <table class="min-w-full divide-y divide-n-weak">
          <thead class="bg-n-alpha-1">
            <tr>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-left"
              >
                {{ t('CHATBOT.table.flow') }}
              </th>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-center"
              >
                {{ t('CHATBOT.table.type') }}
              </th>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-left"
              >
                {{ t('CHATBOT.table.trigger') }}
              </th>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-center"
              >
                {{ t('CHATBOT.table.status') }}
              </th>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-center"
              >
                {{ t('CHATBOT.table.executions') }}
              </th>
              <th
                class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-n-slate-10 text-right"
              >
                {{ t('CHATBOT.table.actions') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="!filteredFlows.length">
              <td
                colspan="6"
                class="px-4 py-10 text-sm text-center text-n-slate-10"
              >
                {{ t('CHATBOT.toolbar.noResults') }}
              </td>
            </tr>
            <tr v-for="f in pagedFlows" :key="f.id" class="hover:bg-n-alpha-1">
              <!-- flow -->
              <td class="px-4 py-1.5">
                <div class="flex items-start">
                  <div
                    class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-blue-100 dark:bg-blue-900/40"
                  >
                    <svg
                      class="w-4 h-4 text-n-blue-11 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect width="8" height="8" x="3" y="3" rx="2" />
                      <path d="M7 11v4a2 2 0 0 0 2 2h4" />
                      <rect width="8" height="8" x="13" y="13" rx="2" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <input
                      v-if="renamingId === f.id"
                      v-model="renameValue"
                      class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-64"
                      autofocus
                      @blur="commitRename(f)"
                      @keyup.enter="commitRename(f)"
                      @keyup.esc="renamingId = null"
                    />
                    <div v-else class="flex items-center gap-1.5 group">
                      <span class="font-medium text-n-slate-12">{{
                        f.name
                      }}</span>
                      <button
                        class="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                        :title="t('CHATBOT.actions.rename')"
                        @click="startRename(f)"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          class="w-4 h-4 shrink-0"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                          />
                        </svg>
                      </button>
                    </div>
                    <input
                      v-if="editingDescId === f.id"
                      v-model="descValue"
                      class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-64 mt-1"
                      :placeholder="t('CHATBOT.description.placeholder')"
                      autofocus
                      @blur="commitDesc(f)"
                      @keyup.enter="commitDesc(f)"
                      @keyup.esc="editingDescId = null"
                    />
                    <button
                      v-else
                      class="block mt-0.5 text-xs text-left text-n-slate-10 hover:text-n-slate-11"
                      @click="startDesc(f)"
                    >
                      {{
                        f.description || t('CHATBOT.description.addDescription')
                      }}
                    </button>
                  </div>
                </div>
              </td>
              <!-- tipo -->
              <td class="px-4 py-1.5 text-center">
                <span
                  class="px-2 py-0.5 text-[0.625rem] rounded-full"
                  :class="
                    f.flow_type === 'sequence'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  "
                  >{{ typeLabel(f.flow_type) }}</span
                >
              </td>
              <!-- gatilho -->
              <td class="px-4 py-1.5 text-sm text-n-slate-11">
                {{ triggerLabel(f) }}
              </td>
              <!-- status -->
              <td class="px-4 py-1.5 text-center">
                <button
                  class="relative w-11 h-6 rounded-full p-0 transition-colors"
                  :class="f.is_active ? 'bg-n-brand' : 'bg-n-slate-5'"
                  @click="toggleActive(f)"
                >
                  <span
                    class="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-[left]"
                    :class="f.is_active ? 'left-[22px]' : 'left-0.5'"
                  />
                </button>
              </td>
              <!-- execuções -->
              <td class="px-4 py-1.5 text-center">
                <button
                  class="inline-flex items-center gap-1 text-sm text-n-blue-11 hover:brightness-110"
                  @click="openHistory(f)"
                >
                  <svg
                    class="w-4 h-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <span class="font-medium">{{ f.executions_count || 0 }}</span>
                </button>
              </td>
              <!-- ações -->
              <td class="px-4 py-1.5 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button
                    v-if="f.flow_type === 'sequence'"
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-blue-11 hover:bg-n-alpha-1"
                    :title="t('SEQUENCES.enroll.button')"
                    @click="openEnroll(f)"
                  >
                    <span class="i-lucide-user-plus w-4 h-4 shrink-0" />
                  </button>
                  <button
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                    :title="t('CHATBOT.actions.editInbox')"
                    @click="openEditInbox(f)"
                  >
                    <svg
                      class="w-4 h-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                    :title="t('CHATBOT.actions.duplicate')"
                    @click="openDuplicate(f)"
                  >
                    <svg
                      class="w-4 h-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                  <button
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                    :title="t('CHATBOT.actions.editFlow')"
                    @click="openBuilder(f)"
                  >
                    <svg
                      class="w-4 h-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                    :title="t('CHATBOT.actions.delete')"
                    @click="remove(f)"
                  >
                    <svg
                      class="w-4 h-4 shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- paginação -->
        <div
          v-if="filteredFlows.length"
          class="flex items-center justify-between px-4 py-2.5 border-t border-n-weak"
        >
          <span class="text-xs text-n-slate-10">
            {{
              t('CHATBOT.toolbar.showing', {
                from: pageFrom,
                to: pageTo,
                total: filteredFlows.length,
              })
            }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="currentPage <= 1"
              :title="t('CHATBOT.toolbar.prev')"
              @click="currentPage > 1 && currentPage--"
            >
              <svg
                class="w-4 h-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <span class="text-xs text-n-slate-11">
              {{
                t('CHATBOT.toolbar.page', {
                  current: currentPage,
                  total: totalPages,
                })
              }}
            </span>
            <button
              class="inline-flex items-center justify-center w-7 h-7 rounded-md text-n-slate-11 hover:bg-n-alpha-1 disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="currentPage >= totalPages"
              :title="t('CHATBOT.toolbar.next')"
              @click="currentPage < totalPages && currentPage++"
            >
              <svg
                class="w-4 h-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- modal criar -->
    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showCreate = false"
    >
      <div
        class="w-[28rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('CHATBOT.createModal.title') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="showCreate = false"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          <div
            v-if="formError"
            class="p-3 border rounded-lg bg-red-50 border-red-200 text-sm text-red-800 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800"
          >
            {{ formError }}
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CHATBOT.createModal.nameLabel')
            }}</label>
            <input
              v-model="form.name"
              class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-full"
              :placeholder="t('CHATBOT.createModal.namePlaceholder')"
              @keyup.enter="createFlow"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CHATBOT.createModal.descriptionLabel')
            }}</label>
            <textarea
              v-model="form.description"
              rows="2"
              class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-full"
              :placeholder="t('CHATBOT.createModal.descriptionPlaceholder')"
            />
          </div>
          <div>
            <label
              class="block mb-1 text-sm font-medium text-n-slate-12 mb-2 block"
              >{{ t('CHATBOT.createModal.typeLabel') }}</label
            >
            <div class="grid grid-cols-2 gap-3">
              <button
                type="button"
                class="p-4 text-left rounded-lg border-2 cursor-pointer transition-colors"
                :class="
                  form.type === 'chatbot'
                    ? 'border-n-brand bg-n-alpha-2'
                    : 'border-n-weak bg-n-background hover:border-n-slate-6'
                "
                @click="form.type = 'chatbot'"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-2xl"
                    ><svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="w-6 h-6"
                    >
                      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" /></svg></span
                  ><span class="font-semibold text-n-slate-12">{{
                    t('CHATBOT.flowType.chatbot')
                  }}</span>
                </div>
                <p class="text-xs text-n-slate-11">
                  {{ t('CHATBOT.createModal.chatbotDescription') }}
                </p>
              </button>
              <button
                type="button"
                class="p-4 text-left rounded-lg border-2 cursor-pointer transition-colors"
                :class="
                  form.type === 'sequence'
                    ? 'border-n-brand bg-n-alpha-2'
                    : 'border-n-weak bg-n-background hover:border-n-slate-6'
                "
                @click="form.type = 'sequence'"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-2xl"
                    ><svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      class="w-6 h-6"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4 12a8 8 0 0 1 13.66-5.66L20 8M20 8V4M20 8h-4"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M20 12a8 8 0 0 1-13.66 5.66L4 16M4 16v4M4 16h4"
                      /></svg></span
                  ><span class="font-semibold text-n-slate-12">{{
                    t('CHATBOT.flowType.sequence')
                  }}</span>
                </div>
                <p class="text-xs text-n-slate-11">
                  {{ t('CHATBOT.createModal.sequenceDescription') }}
                </p>
              </button>
            </div>
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CHATBOT.createModal.inboxLabel')
            }}</label>
            <p class="text-xs text-n-slate-9 mb-1.5">
              {{ t('CHATBOT.createModal.inboxHint') }}
            </p>
            <KanbanMultiSelect
              v-model="form.inboxIds"
              :options="inboxOptions"
              align="top"
              :placeholder="t('CHATBOT.createModal.inboxLabel')"
            />
            <p v-if="!inboxes.length" class="mt-1.5 text-xs text-n-slate-9">
              {{ t('CHATBOT.inboxSelect.noInboxes') }}
            </p>
          </div>
          <div class="flex gap-3 pt-2">
            <button
              class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 flex-1"
              :disabled="creating"
              @click="showCreate = false"
            >
              {{ t('CHATBOT.createModal.cancel') }}
            </button>
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1 justify-center"
              :disabled="creating"
              @click="createFlow"
            >
              {{
                creating
                  ? t('CHATBOT.createModal.creating')
                  : t('CHATBOT.createModal.create')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal editar caixa -->
    <div
      v-if="editInboxFlow"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="editInboxFlow = null"
    >
      <div
        class="w-[28rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh]"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-heading-2 text-n-slate-12">
              {{ t('CHATBOT.editInboxModal.title') }}
            </h2>
            <p class="text-sm text-n-slate-11">{{ editInboxFlow.name }}</p>
          </div>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="editInboxFlow = null"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          <KanbanMultiSelect
            v-model="editInboxIds"
            :options="inboxOptions"
            :placeholder="t('CHATBOT.createModal.inboxLabel')"
          />
          <div class="flex gap-3">
            <button
              class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 flex-1"
              @click="editInboxFlow = null"
            >
              {{ t('CHATBOT.editInboxModal.cancel') }}</button
            ><button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1 justify-center"
              @click="saveEditInbox"
            >
              {{ t('CHATBOT.editInboxModal.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal duplicar -->
    <div
      v-if="duplicateFlow"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="duplicateFlow = null"
    >
      <div
        class="w-[28rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('CHATBOT.duplicateModal.title') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="duplicateFlow = null"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('CHATBOT.duplicateModal.nameLabel')
            }}</label
            ><input
              v-model="duplicateName"
              class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-full"
              @keyup.enter="saveDuplicate"
            />
          </div>
          <div class="flex gap-3">
            <button
              class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 flex-1"
              @click="duplicateFlow = null"
            >
              {{ t('CHATBOT.duplicateModal.cancel') }}</button
            ><button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1 justify-center"
              @click="saveDuplicate"
            >
              {{ t('CHATBOT.duplicateModal.duplicate') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal variáveis de flow -->
    <div
      v-if="showVars"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showVars = false"
    >
      <div
        class="w-[30rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('CHATBOT.variablesModal.title') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="showVars = false"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          <p class="text-sm text-n-slate-11">
            {{ t('CHATBOT.variablesModal.subtitle') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <input
              v-model="newVar.name"
              class="flex-1 min-w-[120px] px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('CHATBOT.variablesModal.namePlaceholder')"
              @keyup.enter="addVariable"
            />
            <input
              v-model="newVar.default_value"
              class="flex-1 min-w-[120px] px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('CHATBOT.variablesModal.defaultPlaceholder')"
            />
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
              :disabled="!newVar.name.trim()"
              @click="addVariable"
            >
              {{ t('CHATBOT.variablesModal.add') }}
            </button>
          </div>
          <input
            v-model="newVar.description"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('CHATBOT.variablesModal.descPlaceholder')"
          />
          <div class="space-y-1.5">
            <p
              v-if="!variables.length"
              class="py-2 text-sm text-center text-n-slate-10"
            >
              {{ t('CHATBOT.variablesModal.empty') }}
            </p>
            <div
              v-for="v in variables"
              :key="v.id"
              class="flex items-center gap-2 p-2.5 rounded-lg border border-n-weak"
            >
              <code class="text-xs font-medium text-n-blue-11">{{
                braced(v.name)
              }}</code>
              <span v-if="v.default_value" class="text-xs text-n-slate-10">
                = {{ v.default_value }}</span
              >
              <span
                v-if="v.description"
                class="flex-1 text-xs truncate text-n-slate-10"
                >{{ v.description }}</span
              >
              <button
                class="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                @click="removeVariable(v)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="showVars = false"
            >
              {{ t('CHATBOT.variablesModal.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal credenciais de IA -->
    <div
      v-if="showAICreds"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showAICreds = false"
    >
      <div
        class="w-[28rem] max-w-full bg-n-background rounded-xl shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('CHATBOT.aiCredentialsModal.title') }}
          </h2>
          <button
            class="text-n-slate-10 hover:text-n-slate-12"
            @click="showAICreds = false"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-4 h-4"
            >
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          <p class="text-sm text-n-slate-11">
            {{ t('CHATBOT.aiCredentialsModal.intro') }}
          </p>

          <!-- abas Texto / Áudio / Imagem -->
          <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1">
            <button
              v-for="tab in AI_TABS"
              :key="tab.id"
              class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
              :class="
                activeAiTab === tab.id
                  ? 'bg-n-background text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeAiTab = tab.id"
            >
              {{ t('CHATBOT.aiCredentialsModal.tabs.' + tab.id) }}
            </button>
          </div>

          <!-- conexão OAuth da conta ChatGPT (só na aba texto) -->
          <div
            v-if="activeAiTab === 'text'"
            class="p-3 rounded-lg border border-n-violet-6 bg-n-violet-2"
          >
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                🧠 {{ t('CHATBOT.aiCredentialsModal.oauth.title') }}
              </span>
              <span
                v-if="oauthStatus.connected"
                class="inline-flex items-center gap-1 px-2 py-0.5 text-[0.625rem] rounded-full bg-n-teal-3 text-n-teal-11"
                >{{ t('CHATBOT.aiCredentialsModal.oauth.connected') }}</span
              >
            </div>
            <p class="text-xs text-n-slate-10 mb-2">
              {{ t('CHATBOT.aiCredentialsModal.oauth.hint') }}
            </p>

            <!-- conectado -->
            <div
              v-if="oauthStatus.connected && oauthStep === 'idle'"
              class="flex items-center justify-between gap-2"
            >
              <span class="text-xs text-n-slate-11 truncate">
                {{ t('CHATBOT.aiCredentialsModal.oauth.accountLabel') }}
                <code class="text-n-slate-12">{{
                  oauthStatus.openai_account_id || '—'
                }}</code>
              </span>
              <button
                class="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-n-ruby-6 text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
                @click="disconnectOauth"
              >
                {{ t('CHATBOT.aiCredentialsModal.oauth.disconnect') }}
              </button>
            </div>

            <!-- passo: colar URL de callback -->
            <div v-else-if="oauthStep === 'waiting_paste'" class="space-y-2">
              <p class="text-xs text-n-slate-11">
                {{ t('CHATBOT.aiCredentialsModal.oauth.pasteInstruction') }}
              </p>
              <input
                v-model="oauthCallbackUrl"
                type="text"
                class="reset-base w-full h-9 px-3 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10"
                placeholder="http://localhost:1455/auth/callback?code=..."
                @keyup.enter="exchangeOauth"
              />
              <div class="flex items-center gap-2">
                <button
                  class="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1"
                  :disabled="oauthBusy || !oauthCallbackUrl.trim()"
                  @click="exchangeOauth"
                >
                  {{ t('CHATBOT.aiCredentialsModal.oauth.finish') }}
                </button>
                <button
                  class="inline-flex items-center justify-center h-9 px-3.5 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                  @click="cancelOauth"
                >
                  {{ t('CHATBOT.aiCredentialsModal.oauth.cancel') }}
                </button>
              </div>
            </div>

            <!-- desconectado -->
            <button
              v-else
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
              :disabled="oauthBusy"
              @click="connectOauth"
            >
              {{ t('CHATBOT.aiCredentialsModal.oauth.connect') }}
            </button>

            <p v-if="oauthError" class="mt-1.5 text-xs text-n-ruby-11">
              {{ oauthError }}
            </p>
          </div>

          <div
            v-for="prov in currentProviders"
            :key="activeAiTab + ':' + prov.id"
            class="p-3.5 rounded-lg border border-n-weak space-y-2.5"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">{{
                prov.label
              }}</span>
              <span
                v-if="aiConfigured(prov.id, activeAiTab)"
                class="inline-flex items-center gap-1 px-2 py-0.5 text-[0.625rem] rounded-full bg-n-teal-3 text-n-teal-11"
                >{{ t('CHATBOT.aiCredentialsModal.configured') }}</span
              >
            </div>
            <input
              v-model="aiKeys[activeAiTab + ':' + prov.id]"
              type="password"
              class="reset-base w-full h-9 px-3 text-sm rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10"
              :placeholder="
                aiConfigured(prov.id, activeAiTab)
                  ? t('CHATBOT.aiCredentialsModal.replaceKey')
                  : t('CHATBOT.aiCredentialsModal.apiKeyPlaceholder')
              "
              @keyup.enter="saveAiCred(prov.id, activeAiTab)"
            />
            <div class="flex items-center gap-2">
              <button
                class="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60 flex-1"
                :disabled="!(aiKeys[activeAiTab + ':' + prov.id] || '').trim()"
                @click="saveAiCred(prov.id, activeAiTab)"
              >
                {{ t('CHATBOT.aiCredentialsModal.save') }}
              </button>
              <button
                v-if="activeAiTab === 'text' && aiConfigured(prov.id, activeAiTab)"
                class="inline-flex items-center justify-center h-9 px-3.5 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                @click="testAiCred(prov.id, activeAiTab)"
              >
                {{ t('CHATBOT.aiCredentialsModal.test') }}
              </button>
              <button
                v-if="aiConfigured(prov.id, activeAiTab)"
                class="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-lg border border-n-ruby-6 text-n-ruby-11 bg-n-ruby-3 hover:bg-n-ruby-4"
                :title="t('CHATBOT.aiCredentialsModal.remove')"
                @click="removeAiCred(prov.id, activeAiTab)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="w-4 h-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6"
                  />
                </svg>
              </button>
            </div>
            <p
              v-if="aiStatus[activeAiTab + ':' + prov.id]?.kind === 'ok'"
              class="mt-1 text-xs text-n-teal-11"
            >
              {{ t('CHATBOT.aiCredentialsModal.testOk') }}
            </p>
            <p
              v-else-if="aiStatus[activeAiTab + ':' + prov.id]?.kind === 'saved'"
              class="mt-1 text-xs text-n-teal-11"
            >
              {{ t('CHATBOT.aiCredentialsModal.savedOk') }}
            </p>
            <p
              v-else-if="
                aiStatus[activeAiTab + ':' + prov.id]?.kind === 'testing' ||
                aiStatus[activeAiTab + ':' + prov.id]?.kind === 'saving'
              "
              class="mt-1 text-xs text-n-slate-10"
            >
              {{ t('CHATBOT.aiCredentialsModal.working') }}
            </p>
            <p
              v-else-if="aiStatus[activeAiTab + ':' + prov.id]?.kind === 'error'"
              class="mt-1 text-xs text-n-ruby-11"
            >
              {{ aiStatus[activeAiTab + ':' + prov.id].msg }}
            </p>
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('CHATBOT.aiCredentialsModal.usedByAiNodes') }}
          </p>
          <div class="flex justify-end">
            <button
              class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="showAICreds = false"
            >
              {{ t('CHATBOT.aiCredentialsModal.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal matricular contatos (sequências) -->
    <div
      v-if="enrollFor"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="enrollFor = null"
    >
      <div
        class="w-[28rem] max-w-full p-6 bg-n-background rounded-xl shadow-xl"
      >
        <h2 class="mb-1 text-heading-2 text-n-slate-12">
          {{ t('SEQUENCES.enroll.title') }}
        </h2>
        <p class="mb-4 text-sm text-n-slate-11">{{ enrollFor.name }}</p>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('SEQUENCES.enroll.inbox')
        }}</label>
        <select
          v-model="enrollInbox"
          class="w-full pl-3 pr-9 py-2 mb-3 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
        >
          <option v-for="ib in inboxes" :key="ib.id" :value="ib.id">
            {{ ib.name }}
          </option>
        </select>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('SEQUENCES.enroll.phones')
        }}</label>
        <textarea
          v-model="enrollPhones"
          rows="4"
          :placeholder="t('SEQUENCES.enroll.phonesPlaceholder')"
          class="w-full px-3 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-alpha-1 text-n-slate-12"
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="inline-flex items-center justify-center px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="enrollFor = null"
          >
            {{ t('SEQUENCES.enroll.cancel') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
            :disabled="enrolling"
            @click="submitEnroll"
          >
            {{ t('SEQUENCES.enroll.submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
